import React, { useEffect, useRef, useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Search } from "lucide-react";

const KEY = process.env.REACT_APP_GIPHY_API_KEY;
const GIPHY = "https://api.giphy.com/v1/gifs";
const PAGE = 24;

const gridUrl = (g) => g?.images?.fixed_width_downsampled?.url || g?.images?.fixed_width?.url;
const fullUrl = (g) => g?.images?.original?.url || g?.images?.fixed_width?.url;

export function GifPicker({ onSelect, trigger }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [gifs, setGifs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const timer = useRef(null);
  const abortRef = useRef(null);

  const load = async (term) => {
    if (!KEY) { setError("GIF search isn't set up yet."); return; }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true); setError("");
    const endpoint = term.trim() ? `${GIPHY}/search` : `${GIPHY}/trending`;
    const params = new URLSearchParams({ api_key: KEY, rating: "g", limit: String(PAGE), offset: "0" });
    if (term.trim()) params.set("q", term.trim().slice(0, 50));
    try {
      const res = await fetch(`${endpoint}?${params}`, { signal: controller.signal });
      const body = await res.json();
      if (!res.ok || body.meta?.status !== 200) throw new Error(body.meta?.msg || "GIF error");
      setGifs(body.data || []);
    } catch (e) {
      if (e.name !== "AbortError") setError("Couldn't load GIFs. Try again.");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && gifs.length === 0) load("");
    return () => { abortRef.current?.abort(); clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSearch = (e) => {
    const v = e.target.value; setQ(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => load(v), 350);
  };

  const choose = (g) => {
    const url = fullUrl(g);
    if (url) onSelect(url);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild data-testid="gif-picker-trigger">{trigger}</PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-2 border-2 border-black bg-spotter-cream" data-testid="gif-picker-panel">
        <div className="relative mb-2">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-spotter-maroon/60" />
          <Input autoFocus value={q} onChange={onSearch} placeholder="Search GIFs" maxLength={50}
            data-testid="gif-search-input" className="border-2 border-black pl-8 h-9" />
        </div>
        <div className="h-64 overflow-y-auto grid grid-cols-3 gap-1" data-testid="gif-grid">
          {loading && <div className="col-span-3 flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-spotter-maroon" /></div>}
          {error && !loading && <div className="col-span-3 text-center text-xs text-spotter-maroon py-8">{error}</div>}
          {!loading && !error && gifs.map((g) => (
            <button key={g.id} type="button" onClick={() => choose(g)} data-testid={`gif-tile-${g.id}`}
              className="aspect-square overflow-hidden rounded border border-black/20 hover:ring-2 hover:ring-spotter-gold">
              <img src={gridUrl(g)} alt={g.title || "GIF"} loading="lazy" className="w-full h-full object-cover" />
            </button>
          ))}
          {!loading && !error && gifs.length === 0 && <div className="col-span-3 text-center text-xs text-spotter-maroon py-8">No GIFs found.</div>}
        </div>
        <div className="text-[10px] text-spotter-maroon/60 text-right pt-1 font-bold tracking-wide">POWERED BY GIPHY</div>
      </PopoverContent>
    </Popover>
  );
}
